// src/pages/ActivityLogPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Collapse,
  Stack,
  Pagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  activityLogAPI,
  ActivityLogEntry,
  ActivityLogParams,
} from '../services/api';
import PageTitle from '../components/common/PageTitle';
import { colors } from '../theme/theme';

const PageWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
});

const ContentBox = styled(Box)(({ theme }) => ({
  padding: '1.5rem',
  [theme.breakpoints.down('sm')]: {
    padding: '1rem 0.5rem',
  },
}));

const FilterBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.75rem',
  alignItems: 'flex-end',
  padding: '1rem',
  backgroundColor:
    theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f8ff',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : colors.primary.light}`,
  marginBottom: '1rem',
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '8px',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : colors.primary.light}`,
})) as typeof TableContainer;

const HeaderCell = styled(TableCell)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? colors.primary.dark : colors.primary.main,
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '1.1rem',
  whiteSpace: 'nowrap',
}));

const BodyRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f7faff',
  },
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#eef3ff',
  },
}));

const ValueBox = styled(Box)(({ theme }) => ({
  fontSize: '0.85rem',
  padding: '2px 6px',
  borderRadius: '4px',
  fontFamily: 'monospace',
  marginTop: '2px',
  wordBreak: 'break-word',
}));

const OldValueBox = styled(ValueBox)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(172,57,57,0.2)' : '#ffebee',
  color: theme.palette.mode === 'dark' ? '#ff8a80' : '#b71c1c',
}));

const NewValueBox = styled(ValueBox)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(44,90,160,0.2)' : '#e8f0fe',
  color: theme.palette.mode === 'dark' ? '#82b1ff' : '#1a3a6b',
}));

const ACTION_COLORS: Record<
  string,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  Created: 'success',
  Updated: 'primary',
  Deleted: 'error',
  Restored: 'warning',
  PermanentlyDeleted: 'error',
  Converted: 'success',
  Emailed: 'info',
  LoggedIn: 'info',
  LoggedOut: 'default',
  LoginFailed: 'error',
  LoginBlocked: 'error',
};

const ENTITY_TYPES = [
  'Auth',
  'Booking',
  'BookingForm',
  'Quote',
  'Client',
  'Pet',
  'Payment',
  'User',
  'PetType',
  'PetSize',
  'PetBreed',
  'PetColour',
  'Vet',
  'Kennel',
  'KennelSize',
  'KennelSide',
  'SystemSettings',
];

const ACTIONS = [
  'Created',
  'Updated',
  'Deleted',
  'Restored',
  'PermanentlyDeleted',
  'Converted',
  'Emailed',
  'LoggedIn',
  'LoggedOut',
  'LoginFailed',
  'LoginBlocked',
];

const PAGE_SIZE = 50;

const ActivityLogPage: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(true);

  const [username, setUsername] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const fetchLogs = useCallback(
    async (pageNum = 1) => {
      setLoading(true);
      try {
        const params: ActivityLogParams = {
          pageNumber: pageNum,
          pageSize: PAGE_SIZE,
          ...(username.trim() && { username: username.trim() }),
          ...(action && { action }),
          ...(entityType && { entityType }),
          ...(fromDate && { fromDate }),
          ...(toDate && { toDate }),
        };
        const result = await activityLogAPI.getLogs(params);
        setLogs(result.items);
        setTotalCount(result.totalCount);
        setTotalPages(result.totalPages);
        setPage(result.pageNumber);
      } catch {
        setLogs([]);
      } finally {
        setLoading(false);
      }
    },
    [username, action, entityType, fromDate, toDate]
  );

  useEffect(() => {
    fetchLogs(1);
  }, []);

  const handleSearch = () => {
    fetchLogs(1);
  };

  const handleClear = () => {
    setUsername('');
    setAction('');
    setEntityType('');
    setFromDate('');
    setToDate('');
    setTimeout(() => fetchLogs(1), 0);
  };

  const handlePageChange = (newPage: number) => {
    fetchLogs(newPage);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <PageWrapper>
      <PageTitle title="Activity Log" />

      <ContentBox>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {totalCount > 0 ? `${totalCount} entries` : 'No entries found'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={filtersOpen ? 'Hide filters' : 'Show filters'}>
              <IconButton
                size="small"
                onClick={() => setFiltersOpen((v) => !v)}
              >
                {filtersOpen ? <ExpandLessIcon /> : <FilterIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  size="small"
                  onClick={() => fetchLogs(page)}
                  disabled={loading}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <FilterBar>
            <TextField
              label="Username"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ minWidth: 140 }}
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="activity-log-action-label">Action</InputLabel>
              <Select
                labelId="activity-log-action-label"
                value={action}
                label="Action"
                onChange={(e) => setAction(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {ACTIONS.map((a) => (
                  <MenuItem key={a} value={a}>
                    {a}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="activity-log-entity-type-label">
                Entity Type
              </InputLabel>
              <Select
                labelId="activity-log-entity-type-label"
                value={entityType}
                label="Entity Type"
                onChange={(e) => setEntityType(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {ENTITY_TYPES.map((e) => (
                  <MenuItem key={e} value={e}>
                    {e}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="From"
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />

            <TextField
              label="To"
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 140 }}
            />

            <Stack direction="row" spacing={1}>
              <Chip
                label="Search"
                color="primary"
                onClick={handleSearch}
                clickable
              />
              <Chip
                label="Clear"
                variant="outlined"
                onClick={handleClear}
                clickable
              />
            </Stack>
          </FilterBar>
        </Collapse>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => handlePageChange(newPage)}
              color="primary"
              size="small"
            />
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <StyledTableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <HeaderCell>When</HeaderCell>
                  <HeaderCell>User</HeaderCell>
                  <HeaderCell>Action</HeaderCell>
                  <HeaderCell>Entity</HeaderCell>
                  <HeaderCell>Description</HeaderCell>
                  <HeaderCell>Changes</HeaderCell>
                  <HeaderCell>IP</HeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{ py: 4, color: 'text.secondary' }}
                    >
                      No activity found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <BodyRow key={log.id}>
                      <TableCell
                        sx={{
                          whiteSpace: 'nowrap',
                          fontSize: '0.85rem',
                          color: 'text.secondary',
                        }}
                      >
                        {formatDate(log.createdAt)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {log.username}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          size="small"
                          color={ACTION_COLORS[log.action] ?? 'default'}
                          variant={
                            log.action === 'PermanentlyDeleted' ||
                            log.action === 'LoginFailed' ||
                            log.action === 'LoginBlocked'
                              ? 'filled'
                              : 'outlined'
                          }
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontWeight: 600 }}
                        >
                          {log.entityType}
                        </Typography>
                        {log.entityId && (
                          <Typography
                            variant="body2"
                            component="span"
                            color="text.secondary"
                          >
                            {' '}
                            #{log.entityId}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.9rem' }}>
                        {log.description}
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        {log.oldValue && (
                          <OldValueBox>— {log.oldValue}</OldValueBox>
                        )}
                        {log.newValue && (
                          <NewValueBox>+ {log.newValue}</NewValueBox>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {log.ipAddress ?? ''}
                      </TableCell>
                    </BodyRow>
                  ))
                )}
              </TableBody>
            </Table>
          </StyledTableContainer>
        )}

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => handlePageChange(newPage)}
              color="primary"
            />
          </Box>
        )}
      </ContentBox>
    </PageWrapper>
  );
};

export default ActivityLogPage;
